import * as _ from "lodash";
import { LgConsole } from "@logex/framework/core";
import {
    IGatherFilterIdCallback,
    IGatherFilterNameCallback,
    IGatherFilterOrderCallback,
    INormalizedLogexPivotDefinition,
    IOrderByPerLevelSpecification,
    LogexPivotService
} from "@logex/framework/lg-pivot";

/**
 * Helper class that encapsulates common pivot-related functions.
 * Use [[PivotInstanceManager.create]] method to get an instance.
 * PivotInstance allows for pivot building, refiltering and calculations execution.
 * Everything is done in synchrounous manner, so you don't have to use post-filter callbacks.
 */
export class PivotInstance<TTopRow = any, TTotals = any> {
    private _orderBy: string | string[];
    private _orderPerLevel: IOrderByPerLevelSpecification;
    // ----------------------------------------------------------------------------------
    // Fields
    context: any;
    contextFn: () => any;
    all: TTopRow[];
    filtered: TTopRow[];
    totals: TTotals;
    lastData: any[];

    /**
     * Gets fired *before* build.
     */

    // ----------------------------------------------------------------------------------
    //
    constructor(
        public definition: INormalizedLogexPivotDefinition,
        context: any,
        private _pivotService: LogexPivotService,
        private _lgConsole: LgConsole
    ) {
        if (_.isFunction(context)) {
            this.contextFn = context as () => any;
        } else {
            this.context = context;
        }
        this.totals = {} as TTotals;
        this.orderBy = [];
        this.orderPerLevel = [[]];
    }

    get orderBy(): string | string[] {
        return this._orderBy;
    }

    set orderBy(value: string | string[]) {
        this._orderBy = typeof value === "string" ? [value] : value;
    }

    get orderPerLevel(): IOrderByPerLevelSpecification {
        return this._orderPerLevel;
    }

    set orderPerLevel(value: IOrderByPerLevelSpecification) {
        this._orderPerLevel = typeof value === "string" ? [value] : value;
    }

    // ----------------------------------------------------------------------------------
    //
    private getContext() {
        return this.contextFn != null ? this.contextFn() : this.context;
    }

    public getLastData<TData>(): TData[] {
        return this.lastData as TData[];
    }

    public mergeWithLastData(data: unknown[]): void {
        if (this.definition.children) {
            for (const row of data) {
                this._pivotService.removeLeafNode(
                    this.definition.children,
                    this.all,
                    row,
                    this.context
                );
                this._pivotService.reattachLeafNode(this.definition, this.all, row, this.context);
            }
        } else {
            for (const row of data) {
                const key = this.definition.mergedKey?.(row, this, null) ?? this.definition.column;

                const index = this.all.findIndex(x => x[key] === row[key]);
                if (index > -1) {
                    this.all.splice(index, 1);
                }
                const nodes = this._pivotService.build(this.definition, [row], null, this.context);
                this._pivotService.reattachLeafNode(
                    this.definition,
                    this.all,
                    nodes[0],
                    this.context
                );
            }
        }
        this.refilter();
    }

    /**
     * Builds pivot out of given data.
     *
     * @param data Source data
     * @param noFiltering Optional flag that can be used to temporary supress refiltering
     * and calculations, if you want to perform them later by explicit call.
     */
    build(data: any[], noFiltering = false) {
        this._lgConsole.debug("PivotInstance.build");

        // this._onBuild.emit();

        this.totals = {} as TTotals;
        this.lastData = data;
        this.all = this._pivotService.build(this.definition, data, this.totals, this.getContext());
        if (!noFiltering) {
            this.refilter();
        }
    }

    /**
     * Refilters the pivot, performs calculations and sorting and then updates the rerender trigger.
     */
    refilter() {
        if (!this.all) return;
        this.filtered = this._pivotService.filter(this.definition, this.all, this.getContext());
        this.calculate();
        this.sort(); // Also renders the pivot
    }

    /**
     * Runs calculations.
     *
     * @param calculationName Name of the calculation to perform.
     * @param unfiltered True if it should be calculation over unfiltered data.
     */
    calculate(calculationName?: string, unfiltered?: boolean) {
        this.totals = this._pivotService.evaluateCalculations(
            this.definition,
            this.all,
            this.filtered,
            {},
            calculationName,
            unfiltered,
            this.getContext()
        );
    }

    /**
     * Sorts the data and rerenders the pivot table.
     */
    sortPerLevel(sortFiltered = true, sortUnfiltered = false) {
        if (!this.all) return;

        if (sortUnfiltered) {
            this.all = this._pivotService.orderBy(this.definition, this.all, this.orderPerLevel[0]);
        }
        if (sortFiltered) {
            this.filtered = this._pivotService.orderByFilteredPerLevel(
                this.definition,
                this.filtered,
                this.orderPerLevel
            );
        }
        // this.rerender();
    }

    sort(sortFiltered = true, sortUnfiltered = false) {
        if (!this.all) return;

        if (sortUnfiltered) {
            this.all = this._pivotService.orderBy(this.definition, this.all, this.orderBy);
        }
        if (sortFiltered) {
            this.filtered = this._pivotService.orderByFiltered(
                this.definition,
                this.filtered,
                this.orderBy
            );
        }
        // this.rerender();
    }

    /**
     * Removes the given leaf node from the pivot.
     *
     * @param node Leaf node from this pivot to be removed.
     */
    removeLeafNode(node) {
        if (!this.all) return;

        this._pivotService.removeLeafNode(this.definition, this.all, node, true, this.getContext());
    }

    /**
     * Attaches the given node to the pivot at leaf leavel and if necessary
     *     creates all the parent nodes.
     *
     * @param node Node to attach
     */
    reattachLeafNode(node) {
        if (!this.all) return;

        this._pivotService.reattachLeafNode(this.definition, this.all, node, this.getContext());
    }

    getLeavesOfNode<TTopRow>(
        parent: any[],
        levelId: number,
        calback: (node: TTopRow) => void
    ): void {
        if (!parent) return;
        const levelDef =
            levelId < 0
                ? this.definition
                : this._pivotService.getDefinitionForLevel(this.definition, levelId);
        this._pivotService.eachLeaf(levelDef, parent, true, (node, level, context) =>
            calback(node)
        );
    }

    /**
     * Gets SelectedRow object that tracks currently selected pivot row
     *
     * @param transformKey
     */
    selectedRow<TKey, TRow>(transformKey?: (id: TKey) => any[]) {
        return new SelectedRow<TKey, TRow>(this, transformKey);
    }

    /**
     * Gets filtering options from the given level of the pivot.
     *
     * @param args
     *    level - Level to query.
     *    filter - Name of the filter to be ignored. If not specified, then filter name is assumed to be equal to the ID field name.
     *    id - Name of the ID field or callback that gets ID from a record.
     *    name - Name of the name field or callback that gets it from a record. If not specified, then "id" field is used as name.
     *    order - Field to order by. If not specified, then it gets sorted by name.
     */
    getFilterOptions<T extends number | string>(args: {
        level: number;
        filter: string;
        id: string | IGatherFilterIdCallback<T>;
        name?: string | IGatherFilterNameCallback<T>;
        order?: string | IGatherFilterOrderCallback<T>;
    }) {
        let optionName = args.name;
        if (!optionName) {
            const optionId = args.id;
            if (_.isString(optionId)) {
                optionName = optionId;
            } else {
                optionName = item => <any>optionId(item);
            }
        }

        return this._pivotService.gatherFilterOptionsFromLevelSorted(
            this.definition,
            this.all,
            args.id,
            optionName,
            args.order,
            args.filter,
            args.level,
            this.context
        );
    }

    /**
     * Gets filtering options' IDs.
     *
     * @param args
     *    level - Level to query
     *    filter - Name of the filter to be ignored
     *    id - Name of the ID field.
     */
    getIds<T extends number | string>(args: {
        filter: string;
        level: number;
        id: string | IGatherFilterIdCallback<T>;
    }) {
        return this._pivotService.gatherFilterOptionIdsFromLevel(
            this.definition,
            this.all,
            args.id,
            args.filter,
            args.level,
            this.context
        );
    }
}

export class SelectedRow<TKey, TRow> {
    private _key: TKey;
    private _row: TRow = null;

    constructor(private _master: PivotInstance, private _transformKey?: (id: TKey) => any[]) {
        // _master.onBuild$.subscribe( () => {
        //    this._row = null;
        // });

        if (_transformKey == null) {
            this._transformKey = key => (_.isArray(key) ? key : [key]);
        }
    }

    set key(value: TKey) {
        this._key = value;
        this.updateRowValue();
    }

    get key() {
        return this._key;
    }

    get row() {
        if (this._key == null) return null;
        return this._row;
    }

    updateRowValue(): void {
        if (this.key == null) {
            this._row = null;
        } else {
            const key = this._transformKey(this._key);
            this._row = this._findRow(key);
        }
    }

    private _findRow(key: any[]) {
        let levelDef = this._master.definition;
        let levelData = this._master.all;
        let row;
        for (let i = 0; ; i++) {
            const levelKeyColumn = levelDef.column;
            row = _.find(levelData, { [levelKeyColumn]: key[i] });

            if (row == null) return null;
            if (i === key.length - 1) break;

            levelDef = levelDef.children;
            levelData = row[levelDef.store];
        }
        return row;
    }

    get isEmpty() {
        return this._key == null;
    }
}
